import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    statusUrl: String,
    onboardUrl: String,
    pro: Boolean,
    remaining: Number,
    onboarded: { type: Boolean, default: false },
    streaming: { type: Boolean, default: false },
    mode: { type: String, default: "editor" },
    maxLength: { type: Number, default: 0 },
    fieldId: { type: String, default: "" },
    fieldContext: { type: String, default: "" },
    lang: { type: String, default: "en" },
    entryId: { type: String, default: "" },
    categoryUrl: { type: String, default: "" },
    fieldNames: { type: Object, default: {} },
    errorMsg: { type: String, default: "Something went wrong" },
    autofillDoneMsg: { type: String, default: "AI filled %{fields}" },
    allFilledMsg: { type: String, default: "All fields already filled" },
    writeFirstMsg: { type: String, default: "Write some content first" },
    tooltipRemaining: { type: String, default: "%{name} — %{count} remaining today" },
    tooltipExhausted: { type: String, default: "Daily limit reached" }
  }

  static targets = [
    "polishBtn", "autofillBtn", "customBtn", "customInput",
    "commandBar",
    "resultPanel", "resultContent", "resultActions",
    "acceptBtn", "rejectBtn", "cancelBtn",
    "textarea",
    "titleInput", "summaryInput", "tagHidden", "categorySelect",
    "charCount",
    "nudgeDot",
    "onboardDialog"
  ]

  connect() {
    this.abortController = null
    this.currentActionType = null
    this.streamedContent = ""
    this.selectionStart = null
    this.selectionEnd = null
    this.streamTimeout = null
    this._nudgeTimeout = null
    this._nudgeUsed = false
    this._pendingAction = null
    this.fetchRemainingCount()

    // For compact mode, find the external textarea by ID
    if (this.modeValue === "compact" && this.fieldIdValue) {
      this._externalTextarea = document.getElementById(this.fieldIdValue)
    }

    // Click outside to dismiss command bar
    this._handleClickOutside = (event) => {
      if (!this.hasCommandBarTarget) return
      if (!this.commandBarTarget.classList.contains("is-open")) return
      // Ignore clicks on the toggle button or inside the command bar
      if (this.hasCustomBtnTarget && this.customBtnTarget.contains(event.target)) return
      if (this.commandBarTarget.contains(event.target)) return
      this.hideCommandBar()
    }
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    this.abortStream()
    this.clearStreamTimeout()
    if (this._nudgeTimeout) clearTimeout(this._nudgeTimeout)
    if (this._handleClickOutside) {
      document.removeEventListener("click", this._handleClickOutside)
    }
  }

  fetchRemainingCount() {
    if (!this.statusUrlValue) return

    fetch(this.statusUrlValue, {
      headers: { "Accept": "application/json" }
    })
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (data && data.remaining !== undefined) {
          this.remainingValue = data.remaining
          this.updateRemainingDisplay(data.remaining, data.is_pro)
        }
      })
      .catch(() => {})
  }

  // --- Textarea Resolution ---

  get resolvedTextarea() {
    if (this.modeValue === "compact" && this._externalTextarea) {
      return this._externalTextarea
    }
    return this.hasTextareaTarget ? this.textareaTarget : null
  }

  // --- Command Bar ---

  toggleCommandBar() {
    if (!this.hasCommandBarTarget) return

    const isOpen = this.commandBarTarget.classList.toggle("is-open")
    if (this.hasCustomBtnTarget) {
      this.customBtnTarget.classList.toggle("is-active", isOpen)
    }
    if (isOpen && this.hasCustomInputTarget) {
      this.customInputTarget.focus()
    }
  }

  hideCommandBar() {
    if (this.hasCommandBarTarget) {
      this.commandBarTarget.classList.remove("is-open")
    }
    if (this.hasCustomBtnTarget) {
      this.customBtnTarget.classList.remove("is-active")
    }
    // Reset textarea height for next open
    if (this.hasCustomInputTarget) {
      this.customInputTarget.style.height = ""
    }
  }

  handleCommandBarKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.hideCommandBar()
      return
    }
    // Enter submits, Shift+Enter inserts newline
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.submitCustom()
      return
    }
  }

  autoResizeCommandInput() {
    if (!this.hasCustomInputTarget) return
    const el = this.customInputTarget
    el.style.height = "auto"
    // Clamp to max 4 lines (~80px)
    el.style.height = Math.min(el.scrollHeight, 80) + "px"
  }

  // --- Onboarding ---

  checkOnboarding(pendingAction) {
    if (this.onboardedValue) return false

    this._pendingAction = pendingAction
    if (this.hasOnboardDialogTarget) {
      this.onboardDialogTarget.showModal()
    }
    return true
  }

  dismissOnboarding() {
    this.onboardedValue = true
    if (this.hasOnboardDialogTarget) {
      this.onboardDialogTarget.close()
    }

    // Fire-and-forget: mark onboarded on server
    if (this.onboardUrlValue) {
      fetch(this.onboardUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      }).catch(() => {})
    }

    // Execute the pending action
    if (this._pendingAction) {
      const action = this._pendingAction
      this._pendingAction = null
      action()
    }
  }

  // --- Actions ---

  performPolish() {
    if (this.streamingValue) return
    if (this.checkOnboarding(() => this.performPolish())) return
    this.hideCommandBar()

    const textarea = this.resolvedTextarea
    if (!textarea) return

    const content = this.getContent(textarea)
    if (!content) {
      this.showToast(this.writeFirstMsgValue, "error")
      return
    }

    this.selectionStart = textarea.selectionStart
    this.selectionEnd = textarea.selectionEnd
    this.hasSelection = this.selectionStart !== this.selectionEnd

    const actionType = (this.modeValue === "compact") ? "polish_short" : "polish"
    this.executeAction(actionType, content)
  }

  performAutofill() {
    if (this.streamingValue) return
    if (this.checkOnboarding(() => this.performAutofill())) return
    this.hideCommandBar()

    const textarea = this.resolvedTextarea
    if (!textarea) return

    const content = textarea.value.trim()
    if (!content) {
      this.showToast(this.writeFirstMsgValue, "error")
      return
    }

    // Pre-check: if all metadata fields already filled, skip API call
    const emptyFields = this.getEmptyAutofillFields()
    if (emptyFields.length === 0) {
      this.showToast(this.allFilledMsgValue, "info")
      return
    }

    this._nudgeUsed = true
    this.hideNudge()
    this.executeAction("autofill", content)
  }

  submitCustom() {
    if (!this.hasCustomInputTarget) return

    const prompt = this.customInputTarget.value.trim()
    if (!prompt) return

    if (this.checkOnboarding(() => this.submitCustom())) return

    const textarea = this.resolvedTextarea
    if (!textarea) return

    const content = this.getContent(textarea)
    if (!content) {
      this.showToast(this.writeFirstMsgValue, "error")
      return
    }

    this.selectionStart = textarea.selectionStart
    this.selectionEnd = textarea.selectionEnd
    this.hasSelection = this.selectionStart !== this.selectionEnd

    this.executeAction("custom", content, prompt)
  }

  // --- Core Execution ---

  executeAction(actionType, content, customPrompt = null) {
    if (this.streamingValue) return

    const lang = this.langValue || "en"
    const entryId = this.entryIdValue || null
    const title = this.hasTitleInputTarget ? this.titleInputTarget.value : ""

    this.currentActionType = actionType
    this.streamedContent = ""

    const isAutofill = actionType === "autofill"

    if (!isAutofill) {
      this.showResultPanel()
    }

    this.startStreaming()

    // Build request body
    const body = {
      content,
      action_type: actionType,
      title,
      lang,
      entry_id: entryId
    }

    if (customPrompt) {
      body.custom_prompt = customPrompt
    }

    if (this.fieldContextValue) {
      body.field_context = this.fieldContextValue
    }

    // Start SSE via fetch
    this.abortController = new AbortController()
    this.resetStreamTimeout()

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify(body),
      signal: this.abortController.signal
    })
      .then(response => {
        if (!response.ok || !response.headers.get("content-type")?.includes("text/event-stream")) {
          this.stopStreaming()
          this.hideResultPanel()
          this.showToast(this.errorMsgValue, "error")
          return
        }
        return this.processSSEResponse(response, isAutofill)
      })
      .catch(error => {
        if (error.name === "AbortError") return
        this.stopStreaming()
        this.hideResultPanel()
        this.showToast(this.errorMsgValue, "error")
      })
  }

  async processSSEResponse(response, isAutofill) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop()

        let currentEvent = null
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim()
          } else if (line.startsWith("data: ")) {
            const data = line.slice(6)
            try {
              const parsed = JSON.parse(data)
              this.handleSSEEvent(currentEvent, parsed, isAutofill)
            } catch (e) {
              // Ignore parse errors
            }
            currentEvent = null
          }
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.stopStreaming()
        this.hideResultPanel()
        this.showToast(this.errorMsgValue, "error")
      }
    } finally {
      if (this.streamingValue) {
        this.stopStreaming()
      }
    }
  }

  handleSSEEvent(event, data, isAutofill) {
    switch (event) {
      case "delta":
        this.streamedContent += data.content || ""
        if (!isAutofill && this.hasResultContentTarget) {
          this.resultContentTarget.textContent = this.streamedContent
          this.updateCharCount()
        }
        this.resetStreamTimeout()
        break

      case "done":
        this.stopStreaming()
        if (isAutofill) {
          this.handleAutofillResult(this.streamedContent)
        } else {
          this.showAcceptReject()
          this.updateCharCount()
        }
        if (data.remaining !== undefined) {
          this.remainingValue = data.remaining
          this.updateRemainingDisplay(data.remaining, data.is_pro)
        }
        // Clear custom input and collapse command bar after successful completion
        if (this.hasCustomInputTarget && this.currentActionType === "custom") {
          this.customInputTarget.value = ""
          this.hideCommandBar()
        }
        break

      case "error":
        this.stopStreaming()
        this.hideResultPanel()
        if (data.error === "trial_exhausted" || data.error === "daily_limit_reached") {
          this.updateRemainingDisplay(0, false)
          this.markExhausted()
        }
        this.showToast(data.message || this.errorMsgValue, "error")
        break

      case "info":
        this.showToast(data.message, "info")
        break
    }
  }

  // --- Autofill ---

  getEmptyAutofillFields() {
    const empty = []
    if (this.hasTitleInputTarget && !this.titleInputTarget.value.trim()) empty.push("title")
    if (this.hasSummaryInputTarget && !this.summaryInputTarget.value.trim()) empty.push("summary")
    if (this.hasTagHiddenTarget && !this.tagHiddenTarget.value.trim()) empty.push("tags")
    if (this.hasCategorySelectTarget && !this.categorySelectTarget.value) empty.push("category")
    return empty
  }

  handleAutofillResult(jsonString) {
    let metadata
    try {
      // Strip markdown fences if present
      const cleaned = jsonString.trim().replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "")
      metadata = JSON.parse(cleaned)
    } catch (e) {
      this.showToast(this.errorMsgValue, "error")
      this.resetState()
      return
    }

    const filledFields = []

    // Only fill empty fields
    if (metadata.title && this.hasTitleInputTarget && !this.titleInputTarget.value.trim()) {
      this.titleInputTarget.value = metadata.title
      this.titleInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
      filledFields.push("title")
    }

    if (metadata.summary && this.hasSummaryInputTarget && !this.summaryInputTarget.value.trim()) {
      this.summaryInputTarget.value = metadata.summary
      this.summaryInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
      // Open the advanced section if collapsed
      const details = this.summaryInputTarget.closest("details")
      if (details && !details.open) details.open = true
      filledFields.push("summary")
    }

    if (metadata.tags && Array.isArray(metadata.tags) && this.hasTagHiddenTarget && !this.tagHiddenTarget.value.trim()) {
      const tags = metadata.tags.slice(0, 10)
      this.tagHiddenTarget.value = tags.join(", ")
      this.tagHiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))

      // Re-render tag input chips
      const tagInputController = this.application.getControllerForElementAndIdentifier(
        this.tagHiddenTarget.closest("[data-controller~='tag-input']"),
        "tag-input"
      )
      if (tagInputController) {
        tagInputController.tags = tags
        tagInputController.update()
      }
      filledFields.push("tags")
    }

    // Category: use combobox API if available, fallback to hidden field
    const categoryData = metadata.category
    if (categoryData && categoryData !== "null" && this.hasCategorySelectTarget && !this.categorySelectTarget.value) {
      const categoryName = typeof categoryData === "string" ? categoryData : categoryData.name
      if (categoryName) {
        const combobox = this.getCategoryComboboxController()
        if (combobox) {
          // Use combobox public API — async create if needed
          combobox.selectByNameOrCreate(categoryName).then((success) => {
            if (success) filledFields.push("category")
            if (filledFields.length > 0) {
              this.showToast(this.autofillDoneMsgValue.replace("%{fields}", this.formatFilledFields(filledFields)), "success")
            }
            this.updateNudge()
          })
          this.resetState()
          return
        } else if (this.categoryUrlValue) {
          // Fallback: create via API and set hidden field directly
          this.findOrCreateCategory(categoryData, filledFields)
          this.resetState()
          return
        }
      }
    }

    if (filledFields.length > 0) {
      this.showToast(this.autofillDoneMsgValue.replace("%{fields}", this.formatFilledFields(filledFields)), "success")
    }

    this.updateNudge()
    this.resetState()
  }

  formatFilledFields(fieldKeys) {
    const names = fieldKeys.map(key => this.fieldNamesValue[key] || key)
    const locale = document.documentElement.lang || "en"
    try {
      return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(names)
    } catch {
      return names.join(", ")
    }
  }

  getCategoryComboboxController() {
    if (!this.hasCategorySelectTarget) return null
    const comboboxEl = this.categorySelectTarget.closest("[data-controller~='category-combobox']")
    if (!comboboxEl) return null
    return this.application.getControllerForElementAndIdentifier(comboboxEl, "category-combobox")
  }

  findOrCreateCategory(categoryData, filledFields) {
    const body = typeof categoryData === "string"
      ? { name: categoryData }
      : { name: categoryData.name, slug: categoryData.slug, description: categoryData.description }

    fetch(this.categoryUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify(body)
    })
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (data && this.hasCategorySelectTarget) {
          // Use combobox API if available
          const combobox = this.getCategoryComboboxController()
          if (combobox) {
            combobox.addAndSelect(data.id, data.name)
          } else {
            this.categorySelectTarget.value = data.id
            this.categorySelectTarget.dispatchEvent(new Event("change", { bubbles: true }))
          }
          filledFields.push("category")
        }
        if (filledFields.length > 0) {
          this.showToast(this.autofillDoneMsgValue.replace("%{fields}", this.formatFilledFields(filledFields)), "success")
        }
        this.updateNudge()
      })
      .catch(() => {
        if (filledFields.length > 0) {
          this.showToast(this.autofillDoneMsgValue.replace("%{fields}", this.formatFilledFields(filledFields)), "success")
        }
      })
  }

  // --- Content Helpers ---

  getContent(textarea) {
    if (textarea.selectionStart !== textarea.selectionEnd) {
      return textarea.value.substring(textarea.selectionStart, textarea.selectionEnd)
    }
    return textarea.value.trim() || null
  }

  // --- Result Handling ---

  accept() {
    if (!this.streamedContent) return

    const textarea = this.resolvedTextarea
    if (!textarea) return

    if (this.hasSelection) {
      const before = textarea.value.substring(0, this.selectionStart)
      const after = textarea.value.substring(this.selectionEnd)
      textarea.value = before + this.streamedContent + after
    } else {
      textarea.value = this.streamedContent
    }

    textarea.dispatchEvent(new Event("input", { bubbles: true }))

    this.hideResultPanel()
    this.resetState()
  }

  reject() {
    this.hideResultPanel()
    this.resetState()
  }

  cancel() {
    this.abortStream()
    this.hideResultPanel()
    this.resetState()
  }

  // --- Char Count (compact mode) ---

  updateCharCount() {
    if (!this.hasCharCountTarget || !this.maxLengthValue) return

    const len = this.streamedContent.length
    const max = this.maxLengthValue
    this.charCountTarget.textContent = `${len} / ${max}`
    this.charCountTarget.classList.toggle("is-over", len > max)
  }

  // --- UI State ---

  showResultPanel() {
    if (this.hasResultPanelTarget) {
      this.resultPanelTarget.classList.add("is-open")
      this.resultContentTarget.textContent = ""
      // Auto-scroll so the panel is visible
      requestAnimationFrame(() => {
        this.resultPanelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
      })
    }
  }

  hideResultPanel() {
    if (this.hasResultPanelTarget) {
      this.resultPanelTarget.classList.remove("is-open")
    }
  }

  showAcceptReject() {
    if (this.hasResultActionsTarget) {
      this.resultActionsTarget.classList.add("is-visible")
    }
    if (this.hasCancelBtnTarget) this.cancelBtnTarget.hidden = true
    if (this.hasAcceptBtnTarget) this.acceptBtnTarget.hidden = false
    if (this.hasRejectBtnTarget) this.rejectBtnTarget.hidden = false
  }

  startStreaming() {
    this.streamingValue = true
    this.element.classList.add("is-streaming")
    // Disable action buttons
    if (this.hasPolishBtnTarget) this.polishBtnTarget.disabled = true
    if (this.hasAutofillBtnTarget) this.autofillBtnTarget.disabled = true
    if (this.hasCustomBtnTarget) this.customBtnTarget.disabled = true
    if (this.hasCustomInputTarget) this.customInputTarget.disabled = true
    if (this.hasResultPanelTarget) {
      this.resultContentTarget.classList.add("is-streaming")
    }
    if (this.hasResultActionsTarget) {
      this.resultActionsTarget.classList.add("is-visible")
      if (this.hasCancelBtnTarget) this.cancelBtnTarget.hidden = false
      if (this.hasAcceptBtnTarget) this.acceptBtnTarget.hidden = true
      if (this.hasRejectBtnTarget) this.rejectBtnTarget.hidden = true
    }
  }

  stopStreaming() {
    this.streamingValue = false
    this.element.classList.remove("is-streaming")
    this.clearStreamTimeout()
    // Re-enable action buttons
    if (this.hasPolishBtnTarget) this.polishBtnTarget.disabled = false
    if (this.hasAutofillBtnTarget) this.autofillBtnTarget.disabled = false
    if (this.hasCustomBtnTarget) this.customBtnTarget.disabled = false
    if (this.hasCustomInputTarget) this.customInputTarget.disabled = false
    if (this.hasResultContentTarget) {
      this.resultContentTarget.classList.remove("is-streaming")
    }
  }

  resetState() {
    this.streamedContent = ""
    this.currentActionType = null
    this.hasSelection = false
    this.selectionStart = null
    this.selectionEnd = null
  }

  abortStream() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.stopStreaming()
  }

  // --- Stream Timeout ---

  resetStreamTimeout() {
    this.clearStreamTimeout()
    this.streamTimeout = setTimeout(() => {
      if (this.streamingValue) {
        this.abortStream()
        this.hideResultPanel()
        this.showToast(this.errorMsgValue, "error")
      }
    }, 30000)
  }

  clearStreamTimeout() {
    if (this.streamTimeout) {
      clearTimeout(this.streamTimeout)
      this.streamTimeout = null
    }
  }

  updateRemainingDisplay(remaining, isPro) {
    const buttons = [
      { target: "polishBtn", name: this.hasPolishBtnTarget ? this.polishBtnTarget.getAttribute("data-tooltip-name") || "Polish" : "Polish" },
      { target: "autofillBtn", name: this.hasAutofillBtnTarget ? this.autofillBtnTarget.getAttribute("data-tooltip-name") || "Auto-fill" : "Auto-fill" },
      { target: "customBtn", name: this.hasCustomBtnTarget ? this.customBtnTarget.getAttribute("data-tooltip-name") || "Custom" : "Custom" }
    ]

    for (const btn of buttons) {
      const el = this[`has${btn.target.charAt(0).toUpperCase() + btn.target.slice(1)}Target`]
        ? this[`${btn.target}Target`]
        : null
      if (!el) continue

      if (remaining === 0) {
        el.title = this.tooltipExhaustedValue
      } else if (remaining === -1) {
        el.title = btn.name
      } else {
        el.title = this.tooltipRemainingValue
          .replace("%{name}", btn.name)
          .replace("%{count}", remaining)
      }
    }

    if (remaining === 0) {
      this.markExhausted()
    }
  }

  markExhausted() {
    if (this.hasPolishBtnTarget) {
      this.polishBtnTarget.disabled = true
      this.polishBtnTarget.classList.add("is-exhausted")
    }
    if (this.hasAutofillBtnTarget) {
      this.autofillBtnTarget.disabled = true
      this.autofillBtnTarget.classList.add("is-exhausted")
    }
    if (this.hasCustomBtnTarget) {
      this.customBtnTarget.disabled = true
      this.customBtnTarget.classList.add("is-exhausted")
    }
    if (this.hasCustomInputTarget) {
      this.customInputTarget.disabled = true
    }
    this.hideCommandBar()
    this.hideNudge()
  }

  // --- Nudge Dot ---

  scheduleNudgeCheck() {
    if (this._nudgeUsed) return
    if (this._nudgeTimeout) clearTimeout(this._nudgeTimeout)
    this._nudgeTimeout = setTimeout(() => this.updateNudge(), 2000)
  }

  updateNudge() {
    if (this._nudgeUsed) return
    if (!this.hasNudgeDotTarget) return

    const textarea = this.resolvedTextarea
    if (!textarea) return

    const content = textarea.value.trim()
    const emptyFields = this.getEmptyAutofillFields()

    const lineCount = content.split("\n").length
    if (lineCount >= 5 && emptyFields.length > 0) {
      this.nudgeDotTarget.classList.add("is-visible")
    } else {
      this.nudgeDotTarget.classList.remove("is-visible")
    }
  }

  hideNudge() {
    if (this.hasNudgeDotTarget) {
      this.nudgeDotTarget.classList.remove("is-visible")
    }
  }

  // --- Toast ---

  showToast(message, type = "info") {
    const container = document.querySelector(".toast-container") || this.createToastContainer()
    const toast = document.createElement("div")
    toast.className = `toast toast-${type}`
    toast.setAttribute("data-controller", "toast")
    toast.innerHTML = `
      <span>${this.escapeHtml(message)}</span>
      <button class="toast-close" data-action="toast#dismiss">&times;</button>
    `
    container.appendChild(toast)
  }

  createToastContainer() {
    const container = document.createElement("div")
    container.className = "toast-container"
    document.body.appendChild(container)
    return container
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
